// OtpCode.tsx
import React, { useRef, useState } from "react";
import "./otp-code.css";

type Props = {
    length?: number;               // default 6
    onComplete?: (code: string) => void;
    onChange?: (code: string) => void;
    autoFocus?: boolean;           // default true (focus first box)
    disabled?: boolean;
};

export default function OtpCode({ length = 6, onComplete, onChange, autoFocus = true, disabled = false }: Props) {
    const [digits, setDigits] = useState<string[]>(Array.from({ length }, () => ""));
    const refs = useRef<Array<HTMLInputElement | null>>([]);

    const focus = (i: number) => refs.current[i]?.focus();

    const handleChange = (i: number, val: string) => {
        const v = val.replace(/\D/g, "").slice(-1); // keep last digit
        // if (!v) return;
        const next = [...digits];
        next[i] = v;
        setDigits(next);
        if (i < length - 1) focus(i + 1);
        const code = next.join("");
        onChange?.(code);
        if (code.length === length && !next.includes("")) onComplete?.(code);
    };

    const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key;
        if (key === "Backspace") {
            if (digits[i]) {
                const next = [...digits];
                next[i] = "";
                setDigits(next);
                return;
            }
            if (i > 0) focus(i - 1);
        } else if (key === "ArrowLeft" && i > 0) {
            e.preventDefault(); focus(i - 1);
        } else if (key === "ArrowRight" && i < length - 1) {
            e.preventDefault(); focus(i + 1);
        }
    };

    const handlePaste = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
        if (!pasted) return;
        const next = [...digits];
        for (let k = 0; k < pasted.length && i + k < length; k++) {
            next[i + k] = pasted[k];
        }
        setDigits(next);
        const firstEmpty = next.indexOf("");
        focus(firstEmpty === -1 ? length - 1 : firstEmpty);
        const code = next.join("");
        onChange?.(code);
        if (code.length === length && !next.includes("")) onComplete?.(code);
    };

    return (
        <div className="otp-row" aria-label="Verification code input" role="group">
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={(el) => (refs.current[i] = el)}
                    value={d}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={(e) => handlePaste(i, e)}
                    inputMode="numeric"
                    pattern="\d*"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    autoFocus={autoFocus && i === 0}
                    maxLength={1}
                    disabled={disabled}
                    aria-label={`Digit ${i + 1} of ${length}`}
                    className="otp-box"
                />
            ))}
        </div>
    );
}
